import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { t } from 'i18next';
import BakeasyLinkButton from '../buttons/BakeasyLinkButton';
import BakeasyCheckbox from '../common/BakeasyCheckbox';
import * as TextField from '../textfields/BakeasyTextFields';
import { GRAY_200 } from '../../constants/Colors';
import { BakeasyTableCell } from './tableCell/BakeasyTableCell';
import { BakeasyChip } from '../common/BakeasyChip';
import { prepareDemicalNumber } from '../../utils/ValidationUtils';
import BakeasyTextField from '../textfields/BakeasyTextField';
import * as Regex from '../../constants/Regex';
import i18n from '../../languages/languages';
import { BakeasyAutoUnitOfMeasure } from '../dropdowns/BakeasyAutoComplete';
import { useAppSelector } from '../../redux_store/reduxStore';
import { dropdownDataState } from '../../redux_store/slices/dropdownDataSlice';
import { getCurrency } from '../../utils/commonUtils';
import { getProfileState } from '../../redux_store/slices/GetUserProfileSlice';
import { invoiceState } from '../../redux_store/slices/invoiceSlice';

interface StoreDetails {
  taxes: {
    rate1: number;
    rate2?: number;
  };
}

interface SelectedValue {
  storeDetails: StoreDetails;
  [key: string]: any;
}

interface Data {
  id: number;
  name: string;
  emailId: string;
  mobile: string;
  status: JSX.Element;
  actions: JSX.Element[];
  storeId: string;
  storeName: string;
  supplierName: string;
  addressCity: string;
  tag: string;
  price: number;
  category: string;
  subCategory: string;
  orderId: string;
  totalPrice: number;
  expectedDate: string;
  deliveryDate: string;
  productName: string;
  contact: string;
  recipeBuilder: string;
  duplicate: string;
  dateCreated: string;
  lastOrderDate: string;
  totalOrders: string;
  totalOrderValue: string;
  ingredientName: string;
  measureType: string;
  units: string;
  nutritionName: string;
  dailyvalue: string;
  dateTime: string;
  From: string;
  To: string;
  recsent: string;
  Content: string;
  Priority: string;
  subject: string;
  lastAccess: string;
  stores: string;
  products: string;
  edit: string;
  supplierId: string;
}

export interface HeadCell {
  disablePadding: boolean;
  id: number | keyof Data | string;
  label: string;
  numeric: boolean;
  isChip?: boolean | false;
  sort?: boolean | false;
  disabled?: boolean | false;
}

interface BakeasySimpleTableMainProps {
  headers?: any[];
  headCells?: any[];
  tableData: any;
  rowProps?: number;
  pageNo?: number;
  navigate?: any;
  count?: any;
  refreshPage?: any;
  refreshPageData?: any;
  api?: string;
  handleCheckChanged?: any;
  handleQuantityChanged?: any;
  handleShippedQuantityChanged?: any;
  handleSimpleCheckChanged?: any;
  handleInvoiceQuantityChanged?: any;
  handleIngredientQuantityChanged?: any;
  handleIngredientPriceChanged?: any;
  handleIngredientQuantityEditChanged?: any;
  handleIngredientMeasureTypeChanged?: any;
  type?: string;
  data?: any;
  status?: any;
  orderValue?: any;
}

const BakeasySimpleTable = React.forwardRef(
  (
    props: BakeasySimpleTableMainProps,
    ref: React.ForwardedRef<{
      handleChangePage: (event: unknown, newPage: number) => void;
      onDeleteRow: (id: number) => void;
    }>
  ) => {
    const {
      headers,
      tableData,
      handleCheckChanged,
      handleQuantityChanged,
      handleShippedQuantityChanged,
      handleInvoiceQuantityChanged,
      handleSimpleCheckChanged,
      handleIngredientQuantityChanged,
      handleIngredientPriceChanged,
      handleIngredientMeasureTypeChanged,
      status,
      orderValue,
    } = props;
    const [tableDataList, setTableDataList] = React.useState<any>([]);
    let _dropdownDataState = useAppSelector(dropdownDataState);
    let _userProfile = useAppSelector(getProfileState);
    let { selectedValue } = useAppSelector(invoiceState);

    const handleIngredientMeasureTypeChange = (row: any, value: any) => {
      let newRow = tableDataList.find(
        (item: any) => item.ingredientId === row.ingredientId
      );
      newRow.measureType = value;
      handleIngredientMeasureTypeChanged(newRow || row, value);
    };

    const handleIngredientQuantityChange = (row: any, value: any) => {
      let newRow = tableDataList.find(
        (item: any) => item.ingredientId === row.ingredientId
      );
      newRow.units = value;
      handleIngredientQuantityChanged(newRow || row, value);
    };

    const handleIngredientQuantityEditChange = (row: any, value: any) => {
      let newRow = tableDataList.find(
        (item: any) => item.ingredientId === row.ingredientId
      );
      newRow.isDisable = value;
      handleIngredientQuantityEditChange(newRow || row, value);
    };

    const handleIngredientPriceChange = (row: any, value: any) => {
      let newRow = tableDataList.find(
        (item: any) => item.ingredientId === row.ingredientId
      );
      newRow.price = value;
      handleIngredientPriceChanged(newRow || row, value);
    };

    const handleQuantityChange = (row: any, value: any) => {
      let newRow = tableDataList.find(
        (item: any) => item.productId === row.productId
      );
      const newValue = Number(value);
      newRow.confirmedQuantity = newValue;
      handleQuantityChanged(newRow || row, newValue);
    };

    const handleShippedQuantityChange = (row: any, value: any) => {
      let newRow = tableDataList.find(
        (item: any) => item.productId === row.productId
      );
      const newValue = Number(value);
      newRow.shippingQuantity = newValue;
      handleShippedQuantityChanged(newRow || row, newValue);
    };

    const handleInvoiceQuantityChange = (row: any, value: any) => {
      let newRow = tableDataList.find(
        (item: any) => item.productId === row.productId
      );
      const newValue = Number(value);
      handleInvoiceQuantityChanged(newRow || row, newValue);
    };

    React.useEffect(() => {
      setTableDataList(tableData);
    }, [tableData]);

    function handleSimpleCheckBox(row: any, checked: boolean) {
      let newRow = row;
      if (checked) {
        newRow = tableDataList.find(
          (item: any) => item.orderId === row.orderId
        );
      }
      newRow.checked = checked;
      handleSimpleCheckChanged(newRow || row, checked);
    }

    function handleCheckBox(row: any, checked: boolean, checkboxType: string) {
      let updatedRow = { ...row };

      if (checked) {
        updatedRow.processOrderChecked =
          checkboxType === 'processOrderChecked' ? true : false;
        updatedRow.backOrderChecked =
          checkboxType === 'backOrderChecked' ? true : false;
        updatedRow.cancelOrderChecked =
          checkboxType === 'cancelOrderChecked' ? true : false;
      } else {
        updatedRow[checkboxType] = false;
      }
      handleCheckChanged(updatedRow, checkboxType, checked);
    }

    function CustomTableCell(id: any, row: any, disabled: boolean | false) {
      switch (id) {
        case 'processOrderChecked':
          return (
            <BakeasyCheckbox
              label={''}
              value={row[id]}
              onChange={function (
                event: React.ChangeEvent<HTMLInputElement>
              ): void {
                handleCheckBox(
                  row,
                  event.target.checked,
                  'processOrderChecked'
                );
              }}
            />
          );
        case 'backOrderChecked':
          return (
            <BakeasyCheckbox
              label={''}
              value={row[id]}
              disabled={row['backOrder'] === false}
              // tooltipTitle={t("DOES_NOT_ACCEPT_BACKORDES")}
              tooltipTitle={
                row['backOrder'] === false
                  ? t('DOES_NOT_ACCEPT_BACKORDERS')
                  : ''
              }
              onChange={function (
                event: React.ChangeEvent<HTMLInputElement>
              ): void {
                handleCheckBox(row, event.target.checked, 'backOrderChecked');
              }}
            />
          );
        case 'cancelOrderChecked':
          return (
            <BakeasyCheckbox
              label={''}
              value={row[id]}
              onChange={function (
                event: React.ChangeEvent<HTMLInputElement>
              ): void {
                handleCheckBox(row, event.target.checked, 'cancelOrderChecked');
              }}
            />
          );
        case 'confirmedQuantity':
          if (!disabled) {
            return (
              <TextField.Quantity
                label={''}
                disabled={row?.cancelOrderChecked}
                value={row.confirmedQuantity}
                onChange={(event: any) => {
                  if (parseInt(event.target.value)) {
                    handleQuantityChange(row, event.target.value);
                  } else {
                    handleQuantityChange(row, 0);
                  }
                }}
              />
            );
          } else {
            return row[id];
          }
        case 'shippingQuantity':
          if (!disabled) {
            return (
              <TextField.Quantity
                label={''}
                // label=""
                // required
                // hideRequiredStar
                disabled={row?.cancelOrderChecked}
                value={row.shippingQuantity}
                onChange={(event: any) => {
                  if (parseInt(event.target.value)) {
                    handleShippedQuantityChange(row, event.target.value);
                  } else {
                    handleShippedQuantityChange(row, 0);
                  }
                }}
              />
            );
          } else {
            return row[id];
          }
        case 'invoicedQuantity':
          return (
            <TextField.Quantity
              label={''}
              value={row.invoicedQuantity}
              disabled={true}
              name="number"
              onChange={(event: any) => {
                handleInvoiceQuantityChange(row, event.target.value);
              }}
            />
          );
        case 'name':
          return (
            <BakeasyLinkButton
              label={row[id]}
              style={{ fontWeight: 'bold' }}
              onClick={row['onNameClick']}
            />
          );
        case 'contact':
          return (
            <BakeasyLinkButton
              label={row[id]}
              onClick={row['onContactClick']}
            />
          );
        case 'totalPrice':
        case 'unitPrice':
          return Number(row[id])?.toFixed(2);
        case 'productionCancelOrderchecked':
          return (
            <BakeasyCheckbox
              label={''}
              value={row[id]}
              disabled={row['disabled']}
              onChange={function (
                event: React.ChangeEvent<HTMLInputElement>
              ): void {
                handleSimpleCheckBox(row, event.target.checked);
              }}
            />
          );
        case 'productionId':
          return (
            <BakeasyLinkButton
              label={row[id]}
              onClick={row['onProductionIdClick']}
            />
          );
        case 'productName':
          if (row['status']?.toLowerCase() === 'cancelled') {
            return (
              <>
                {row[id]}
                {<BakeasyChip label={row['status']} />}
              </>
            );
          }
          return row[id];
        case 'units':
          if (!disabled) {
            return (
              <>
                <BakeasyTextField
                  label={''}
                  disabled={row?.isDisabled}
                  type="text"
                  placeHolder={''}
                  name="ingQuantity"
                  fullWidth={false}
                  maxLength={5}
                  value={row.units}
                  onChange={(event: any) => {
                    // if (!ValidateInput(Regex.ingredientQuantity, event?.target?.value)) {
                    //   return false;
                    // }
                    let newValue = event.target.value;
                    if (newValue.startsWith('.')) {
                      newValue = '0' + newValue;
                    }
                    handleIngredientQuantityChange(
                      row,
                      prepareDemicalNumber(event?.target?.value)
                    );
                  }}
                  regexPattern={Regex.decimalNumber}
                />
                {/* <BakeasyTextField
                  label={''}
                  type="text"
                  placeHolder={''}
                  name="quantity"
                  required={false}
                  value={row.quantity}
                  maxLength={4}
                  style={{ height: '35px' }}
                  regexPattern={Regex.quantity}
                  onChange={(event: any) => {
                    const newValue = event.target.value;
                    const formattedValue =
                      newValue.length > 0
                        ? newValue.startsWith('0') && newValue !== '0'
                          ? newValue.slice(1)
                          : newValue
                        : '0';

                    // if (!ValidateInput(Regex.quantity, formattedValue)) {
                    //   return false;
                    // }
                    handleQuantityChange(index, formattedValue);
                  }} 
                />*/}
              </>
            );
          } else {
            return row.units;
          }
        case 'price':
          if (!disabled) {
            return (
              <>
                <BakeasyTextField
                  label={''}
                  disabled={row?.isDisabled}
                  type="text"
                  placeHolder={''}
                  name=""
                  fullWidth={false}
                  maxLength={5}
                  value={row.price}
                  //helperText='enterr'
                  onChange={(event: any) => {
                    // if (!ValidateInput(Regex.ingredientQuantity, event?.target?.value)) {
                    //   return false;
                    // }
                    handleIngredientPriceChange(
                      row,
                      prepareDemicalNumber(event?.target?.value)
                    );
                  }}
                  regexPattern={Regex.decimalNumber}
                />
              </>
            );
          } else {
            return row.price;
          }
        case 'measureType':
          if (typeof row[id] === 'object') {
            // return row[id][i18n.language];
            if (!disabled) {
              return (
                <BakeasyAutoUnitOfMeasure
                  label=""
                  required={false}
                  disabled={row?.isDisabled}
                  value={row[id]}
                  options={_dropdownDataState.value?.data?.UnitOfMeasure}
                  onChange={async (newValue: any) => {
                    if (typeof newValue === 'object') {
                      handleIngredientMeasureTypeChange(row, newValue);
                    }
                  }}
                />
              );
            } else {
              return (
                row[id][i18n.language] || row[id]['measureType'][i18n.language]
              );
            }
          } else {
            return row[id];
          }
        default:
          return row[id];
      }
    }

    function getHeaderLabel(header: any) {
      switch (header?.id) {
        case 'unitPrice':
        case 'totalprice':
        case 'totalPrice':
        case 'price':
          return t(header?.label) + getCurrency(_userProfile?.value);
        default:
          return t(header?.label);
      }
    }

    return (
      <Box sx={{ width: '100%' }}>
        <Paper sx={{ width: '100%' }}>
          <TableContainer>
            <Table
              stickyHeader
              sx={{ minWidth: 500 }}
              aria-labelledby="tableTitle"
            >
              <TableHead>
                <TableRow>
                  {headers?.map((header: any, index: any) => (
                    <TableCell
                      key={index}
                      style={{
                        padding: 10,
                        backgroundColor: GRAY_200,
                        width: header.width ? header.width : 'auto',
                        textAlign: header?.numeric ? 'right' : header.align,
                        maxWidth: '400px',
                      }}
                    >
                      <Typography style={{ fontWeight: 'bold' }}>
                        {getHeaderLabel(header)}
                      </Typography>
                      {/* <Typography style={{ fontWeight: 600 }}>{t(header.label)}</Typography> */}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {tableDataList?.map((row: any, index: any) => (
                  <TableRow key={index} sx={{}}>
                    {headers?.map((header: any, headerIndex: any) => (
                      <TableCell
                        key={headerIndex}
                        style={{
                          padding: 10,
                          width: header.width ? header.width : 'auto',
                          textAlign: header?.numeric ? 'right' : header.align,
                          maxWidth: '400px',
                        }}
                      >
                        {CustomTableCell(header.id, row, header.disabled)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>

              {props?.type === 'orderSummary' && (
                <>
                  <TableRow>
                    <TableCell colSpan={3} />
                    <BakeasyTableCell value={t('SUB_TOTAL')} />
                    <BakeasyTableCell
                      value={Number(props?.data?.calculatedsubTotal).toFixed(2)}
                    />
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={2} />
                    <BakeasyTableCell
                      colSpan={2}
                      value={orderValue?.taxes?.rate1}
                    />
                    <BakeasyTableCell
                      value={Number(props?.data?.calculatedtax1).toFixed(2)}
                    />
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={2} />
                    <BakeasyTableCell
                      colSpan={2}
                      value={orderValue?.taxes?.rate2}
                    />
                    <BakeasyTableCell
                      value={Number(props?.data?.calculatedtax2).toFixed(2)}
                    />
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={2} />
                    <BakeasyTableCell
                      colSpan={2}
                      value={
                        orderValue?.shippingRateType === 'Percentage'
                          ? t('SHIPPING_CHARGE_PERCENTAGE')
                          : orderValue?.shippingRateType === 'Amount'
                            ? t('SHIPPING_CHARGE_AMOUNT')
                            : orderValue?.shippingRateType
                      }
                    />
                    <BakeasyTableCell
                      value={Number(
                        props?.data?.calculatedshippingCharge
                      ).toFixed(2)}
                      //value={orderValue?.shippingRate}
                    />
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={3} />
                    <BakeasyTableCell value={t('TOTAL')} />
                    <BakeasyTableCell
                      value={Number(props?.data?.calculatedtotal).toFixed(2)}
                    />
                  </TableRow>
                </>
              )}
              {/* {props?.type === 'orderDetails' || 'invoiceDetails' && ( */}
              {props?.type === 'orderDetails' &&
                props?.status?.toLowerCase() !== 'cancelled' && (
                  <>
                    <TableRow>
                      <TableCell colSpan={4} />
                      <BakeasyTableCell value={t('SUB_TOTAL')} />
                      <BakeasyTableCell
                        value={Number(props?.data?.calculatedsubTotal).toFixed(
                          2
                        )}
                      />
                    </TableRow>

                    <TableRow>
                      <TableCell colSpan={3} />
                      <BakeasyTableCell
                        colSpan={2}
                        value={orderValue?.taxes?.rate1}
                      />
                      <BakeasyTableCell
                        value={Number(props?.data?.calculatedtax1).toFixed(2)}
                      />
                    </TableRow>

                    <TableRow>
                      <TableCell colSpan={3} />
                      <BakeasyTableCell
                        colSpan={2}
                        value={orderValue?.taxes?.rate2}
                      />
                      <BakeasyTableCell
                        value={Number(props?.data?.calculatedtax2).toFixed(2)}
                      />
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={3} />
                      <BakeasyTableCell
                        colSpan={2}
                        value={
                          orderValue?.shippingRateType === 'Percentage'
                            ? t('SHIPPING_CHARGE_PERCENTAGE')
                            : orderValue?.shippingRateType === 'Amount'
                              ? t('SHIPPING_CHARGE_AMOUNT')
                              : orderValue?.shippingRateType
                        }
                      />
                      <BakeasyTableCell
                        //  value={orderValue?.shippingRate}
                        value={Number(
                          props?.data?.calculatedshippingCharge
                        ).toFixed(2)}
                      />
                    </TableRow>

                    <TableRow>
                      <TableCell colSpan={4} />
                      <BakeasyTableCell value={t('TOTAL')} />
                      <BakeasyTableCell
                        value={Number(props?.data?.calculatedtotal).toFixed(2)}
                      />
                    </TableRow>
                  </>
                )}

              {props?.type === 'shippingDetails' && (
                <>
                  <TableRow>
                    <TableCell colSpan={4} />
                    <BakeasyTableCell value={t('SUB_TOTAL')} />
                    <BakeasyTableCell
                      value={Number(props?.data?.calculatedsubTotal).toFixed(2)}
                    />
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={3} />
                    <BakeasyTableCell
                      colSpan={2}
                      value={orderValue?.taxes?.rate1}
                    />
                    <BakeasyTableCell
                      value={Number(props?.data?.calculatedtax1).toFixed(2)}
                    />
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={3} />
                    <BakeasyTableCell
                      colSpan={2}
                      value={orderValue?.taxes?.rate2}
                    />
                    <BakeasyTableCell
                      value={Number(props?.data?.calculatedtax2).toFixed(2)}
                    />
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={3} />
                    <BakeasyTableCell
                      colSpan={2}
                      value={
                        orderValue?.shippingRateType === 'Percentage'
                          ? t('SHIPPING_CHARGE_PERCENTAGE')
                          : orderValue?.shippingRateType === 'Amount'
                            ? t('SHIPPING_CHARGE_AMOUNT')
                            : orderValue?.shippingRateType
                      }
                    />
                    <BakeasyTableCell
                      //value={calculatedshippingCharge.toFixed(2)}
                      value={Number(
                        orderValue?.amount?.calculatedshippingCharge
                      ).toFixed(2)}
                    />
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={4} />
                    <BakeasyTableCell value={t('TOTAL')} />
                    <BakeasyTableCell
                      value={Number(props?.data?.calculatedtotal).toFixed(2)}
                    />
                  </TableRow>
                </>
              )}

              {props?.type === 'invoiceDetails' && (
                <>
                  <TableRow>
                    <TableCell colSpan={4} />
                    <BakeasyTableCell value={t('SUB_TOTAL')} />
                    <BakeasyTableCell
                      value={Number(props?.data?.calculatedsubTotal).toFixed(2)}
                    />
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={3} />
                    <BakeasyTableCell
                      colSpan={2}
                      value={
                        (orderValue as unknown as SelectedValue)?.storeDetails
                          ?.taxes?.rate1 || '-'
                      }
                    />
                    <BakeasyTableCell
                      value={Number(props?.data?.calculatedtax1).toFixed(2)}
                    />
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={3} />
                    <BakeasyTableCell
                      colSpan={2}
                      value={
                        (orderValue as unknown as SelectedValue)?.storeDetails
                          ?.taxes?.rate2 || '-'
                      }
                    />
                    <BakeasyTableCell
                      value={Number(props?.data?.calculatedtax2).toFixed(2)}
                    />
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={3} />
                    <BakeasyTableCell
                      colSpan={2}
                      value={
                        orderValue?.shippingRateType === 'Percentage'
                          ? t('SHIPPING_CHARGE_PERCENTAGE')
                          : orderValue?.shippingRateType === 'Amount'
                            ? t('SHIPPING_CHARGE_AMOUNT')
                            : orderValue?.shippingRateType
                      }
                    />
                    <BakeasyTableCell
                      value={Number(
                        props?.data?.calculatedshippingCharge
                      ).toFixed(2)}
                      //value={props?.data?.calculatedshippingCharge?.toFixed(2)}
                    />
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={4} />
                    <BakeasyTableCell value={t('TOTAL')} />
                    <BakeasyTableCell
                      value={Number(props?.data?.calculatedtotal).toFixed(2)}
                    />
                  </TableRow>
                </>
              )}
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    );
  }
);

export default BakeasySimpleTable;
